import React, { useState, useRef, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const UaucResolvedDetailHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-resolved-detail"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
  <div className="drawer-header">
    <div className="header-title">
      <span className="title">UAUC Resolve Detail Page – Help Guide</span>
    </div>
    <button className="close-button" onClick={toggle} aria-label="Close help panel">
      &times;
    </button>
  </div>

  <div className="drawer-body">
    <p>
      The <b>UAUC Detail Page</b> provides complete information about a reported 
      <b>Unsafe Act (UA)</b> or <b>Unsafe Condition (UC)</b>, along with supporting 
      details, evidence, resolution history, and analytics.
    </p>

    <h5 className="section-title">
      <span className="emoji">📑</span> Header Information
    </h5>
    <hr />
    <p>At the top of the page, you will see:</p>
    <ul className="steps-list">
      <li>. Record Number (e.g., 940)</li>
      <li>. UAUC Type (Unsafe Act or Unsafe Condition)</li>
      <li>. Activity Name (e.g., Bar Bending)</li>
      <li>. Reported Month (e.g., July)</li>
    </ul>
    <p>Below this, a timeline view shows key milestones:</p>
    <ul className="steps-list">
      <li>. Reported Details</li>
      <li>. Assigned To Details</li>
      <li>. Closing Details</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">📝</span> Reported Details
    </h5>
    <hr />
    <p>This section captures the original report:</p>
    <ul className="steps-list">
      <li>. Evidence (image or file uploaded)</li>
      <li>. Observation submitted</li>
      <li>. Hazard Type</li>
      <li>. Risk Level</li>
      <li>. Source (Rules) under which violation occurred</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">✅</span> Resolved Details
    </h5>
    <hr />
    <p>Once resolved, you can see:</p>
    <ul className="steps-list">
      <li>. Resolve Evidence (image or file)</li>
      <li>. Resolved Remark (notes/comments on closure)</li>
      <li>. Resolved Date</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">📌</span> Activity Info
    </h5>
    <hr />
    <p>Shows contextual details about the UAUC:</p>
    <ul className="steps-list">
      <li>. Service and respective Plant / Project / Client</li>
      <li>. Barrier(s) captured during reporting</li>
      <li>. Sub-Hazard related to the Hazard Type</li>
      <li>. Department / Contractor / Location linked to the report</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">🛡️</span> Control Measures
    </h5>
    <hr />
    <p>
      Lists the violated or missing control measures that were not followed while performing the activity.
    </p>

    <h5 className="section-title">
      <span className="emoji">👥</span> Assigned Details & Team Collaboration
    </h5>
    <hr />
    <ul className="steps-list">
      <li>. Assigned User: The person responsible for resolving the UAUC</li>
      <li>. Invited Team Members: Users added to collaborate on resolution</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">⚠️</span> Violator Details
    </h5>
    <hr />
    <p>Shows the name(s) of violator(s) recorded during UAUC reporting.</p>

    <h5 className="section-title">
      <span className="emoji">📂</span> Document Section
    </h5>
    <hr />
    <p>Upload supporting documents related to the case are shown here.</p>

    <h5 className="section-title">
      <span className="emoji">📈</span> Escalation Details
    </h5>
    <hr />
    <p>
      Displays escalation levels if the UAUC is not resolved by the due date.
    </p>
    <p>
      Example: <b>L1</b> = first-level escalation, as per the escalation matrix.  
      Displayed in an org-chart format for clarity.
    </p>

    <h5 className="section-title">
      <span className="emoji">⏳</span> Deviation
    </h5>
    <hr />
    <p>
      Indicates delays in closure:
    </p>
    <ul className="steps-list">
      <li>. If resolved after the due date, the deviation (in days) is displayed on the card.</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">💬</span> Discussion Section
    </h5>
    <hr />
    <p>A built-in chat space for all team members involved in resolution:</p>
    <ul className="steps-list">
      <li>. Enables collaborative problem-solving</li>
      <li>. Helps design risk reduction strategies for long-term safety improvement</li>
    </ul>

    <h5 className="section-title">
      <span className="emoji">📊</span> Analytics Panel (Right-Hand Side)
    </h5>
    <hr />
    <p>Provides activity-based insights, including:</p>
    <ul className="steps-list">
      <li>. IRA Analysis (accident statistics)</li>
      <li>. Near-Miss Analysis</li>
      <li>. UA vs UC Analysis</li>
      <li>. Risk-Based Analysis</li>
      <li>. Good Act & TBT Count</li>
      <li>. Internal & External Training Count</li>
      <li>. Employee & Contractor Training Count</li>
    </ul>
    <p>
      This data supports <b>data-driven decision-making</b> to reduce future UAUCs.
    </p>

    <p>
      <b>To invite:</b> click the <i>Invite User</i> icon (top bar) → select team member(s) → 
      notification is sent automatically.
    </p>
  </div>
</div>



      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default UaucResolvedDetailHelpDrawer;



